import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, type ReactNode } from 'react'

import { getMeQueryOptions } from '@/src/features/auth/services/useMeQuery'
import type { TNotificationApi } from '@/src/features/notification/api/types/notificationApi.types'
import {
  newPrayerNotificationTypes,
  NOTIFICATION_TYPE,
} from '@/src/features/notification/models/notification.constants'
import type { TNewPrayerNotificationTypes } from '@/src/features/notification/models/notification.types'
import { useNotificationsQuery } from '@/src/features/notification/services/useNotificationsQuery'

import { Cookie, type TCookieName } from '../../utils/cookies/Cookie'
import { PrayerMiniPrompt } from './PrayerMiniPrompt'

const miniPromptCookieName: Record<TNewPrayerNotificationTypes, TCookieName> = {
  [NOTIFICATION_TYPE.NEW_PRAYER_ON_ARRIVAL]: 'arrivalPrayerPrompt',
  [NOTIFICATION_TYPE.NEW_PRAYER_ON_BIRTHDAY]: 'birthdayPrayerPrompt',
  [NOTIFICATION_TYPE.NEW_PRAYER_ON_INTENTION]: 'prayerPromptAndBubble',
}

const isNewPrayerNotificationType = (type: NOTIFICATION_TYPE): type is TNewPrayerNotificationTypes =>
  (newPrayerNotificationTypes as readonly NOTIFICATION_TYPE[]).includes(type)

const isNewPrayerNotification = (
  notification: TNotificationApi,
): notification is TNotificationApi<TNewPrayerNotificationTypes> =>
  // Check that it is a new prayer notification:
  isNewPrayerNotificationType(notification.type) &&
  // Check that is newer that the last time it was displayed:
  new Date(notification.date).valueOf() > Number(Cookie.get(miniPromptCookieName[notification.type]) ?? 0) &&
  // Check that it is really somewhat new: < 12 hours old:
  new Date(notification.date).valueOf() > Date.now() - 12 * 60 * 60 * 1000

export const PrayerPromptManager = (): ReactNode => {
  const { data: notifications = [] } = useNotificationsQuery()
  const queryClient = useQueryClient()

  const newPrayerNotifications = useMemo(() => notifications?.filter(isNewPrayerNotification), [notifications])

  useEffect(() => {
    if (newPrayerNotifications.length > 0) {
      // Invalidate me query to update intention prayers count
      queryClient.invalidateQueries({ queryKey: getMeQueryOptions().queryKey })
    }
  }, [newPrayerNotifications, queryClient])

  useEffect(() => {
    if (newPrayerNotifications.length > 0) {
      ;[...new Set(newPrayerNotifications.map(({ type }) => type))].forEach((type) =>
        Cookie.set(miniPromptCookieName[type], Date.now(), 'oneYear'),
      )
    }
  }, [newPrayerNotifications])

  return <PrayerMiniPrompt notifications={newPrayerNotifications} />
}
